import React, { useEffect, useRef, useState } from "react";

export default function MobileScrollArea({ className = "", children, ariaLabel = "Scrollable data" }) {
  const shellRef = useRef(null);
  const viewportRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [active, setActive] = useState(false);
  const [position, setPosition] = useState({ left: 8, right: 8, top: 0 });

  const update = () => {
    const shell = shellRef.current;
    const el = viewportRef.current;
    if (!shell || !el) return;

    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);

    const mobile = window.matchMedia("(max-width: 640px)").matches;
    const rect = shell.getBoundingClientRect();
    const visibleTop = Math.max(0, rect.top);
    const visibleBottom = Math.min(window.innerHeight, rect.bottom);
    const isVisible = mobile && visibleBottom > visibleTop;
    setActive(isVisible);

    if (isVisible) {
      const visibleHeight = visibleBottom - visibleTop;
      const top = visibleHeight >= 88
        ? Math.max(visibleTop + 44, Math.min(window.innerHeight / 2, visibleBottom - 44))
        : visibleTop + visibleHeight / 2;
      setPosition({
        left: Math.max(8, rect.left + 6),
        right: Math.max(8, window.innerWidth - rect.right + 6),
        top
      });
    }
  };

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (observer) observer.observe(el);

    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      if (observer) observer.disconnect();
    };
  }, [children]);

  const move = (direction) => {
    const el = viewportRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(220, el.clientWidth * 0.78), behavior: "smooth" });
  };

  const shellStyle = {
    "--sx-scroll-left": `${position.left}px`,
    "--sx-scroll-right": `${position.right}px`,
    "--sx-scroll-top": `${position.top}px`
  };

  return (
    <div
      ref={shellRef}
      style={shellStyle}
      className={active ? "sx-mobile-scroll-shell sx-scroll-active" : "sx-mobile-scroll-shell"}
    >
      <button
        type="button"
        className="sx-mobile-scroll-arrow sx-mobile-scroll-left"
        aria-label={`Scroll ${ariaLabel} left`}
        disabled={!canLeft}
        onClick={() => move(-1)}
      >
        ‹
      </button>
      <div className={className} ref={viewportRef} onLoad={update}>
        {children}
      </div>
      <button
        type="button"
        className="sx-mobile-scroll-arrow sx-mobile-scroll-right"
        aria-label={`Scroll ${ariaLabel} right`}
        disabled={!canRight}
        onClick={() => move(1)}
      >
        ›
      </button>
    </div>
  );
}
